import { useEffect, useState, useCallback } from 'react'
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Grid,
  HStack,
  Input,
  VStack,
} from '@chakra-ui/react'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { apiClient } from '@/lib/api/client'

export type AssetTagFormProps = {
  tagId?: number
  onSuccess?: () => void
  onCancel?: () => void
}

const initialValues = {
  name: '',
  slug: '',
  active: true,
}

function slugify(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
}

export function AssetTagForm({ tagId, onSuccess, onCancel }: AssetTagFormProps) {
  const { showError, showSuccess } = useToastNotification()
  const [loading, setLoading] = useState(!!tagId)
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<Record<string, string[]>>({})
  const [form, setForm] = useState(initialValues)
  const [slugTouched, setSlugTouched] = useState(false)

  const loadOne = useCallback(async () => {
    if (!tagId) return
    setLoading(true)
    setErrors({})
    try {
      const { data } = await apiClient.get(`/master-data/tags/${tagId}`)
      const r = data?.data ?? data
      setForm({
        name: r.name ?? '',
        slug: r.slug ?? '',
        active: r.active !== false,
      })
      setSlugTouched(true)
    } catch (e: unknown) {
      showError(e as Error)
    } finally {
      setLoading(false)
    }
  }, [tagId, showError])

  useEffect(() => {
    if (tagId) {
      loadOne()
    } else {
      setForm(initialValues)
      setSlugTouched(false)
    }
  }, [tagId, loadOne])

  const updateField = (field: keyof typeof form, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev }
        delete next[field]
        return next
      })
    }
  }

  const handleNameChange = (value: string) => {
    setForm((prev) => ({
      ...prev,
      name: value,
      slug: slugTouched ? prev.slug : slugify(value),
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrors({})
    setSaving(true)
    try {
      const payload = {
        name: form.name.trim(),
        slug: (form.slug.trim() || slugify(form.name)).trim(),
        active: form.active,
      }
      if (tagId) {
        await apiClient.put(`/master-data/tags/${tagId}`, payload)
        showSuccess('Success', 'Tag updated successfully')
      } else {
        await apiClient.post('/master-data/tags', payload)
        showSuccess('Success', 'Tag created successfully')
      }
      onSuccess?.()
    } catch (err: unknown) {
      const res = (err as { response?: { data?: { errors?: Record<string, string[]> } } })?.response?.data
      if (res?.errors && typeof res.errors === 'object') {
        setErrors(res.errors)
      } else {
        showError(err as Error)
      }
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <Box py={8}>
        <Box as="span">Loading...</Box>
      </Box>
    )
  }

  return (
    <Box as="form" onSubmit={handleSubmit}>
      <VStack spacing={4} align="stretch">
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Name <Box as="span" color="red.500">*</Box>
          </FormLabel>
          <FormControl isInvalid={!!errors.name?.length} isRequired>
            <Input value={form.name} onChange={(e) => handleNameChange(e.target.value)} placeholder="e.g. Fragile, High Priority" />
            <FormErrorMessage>{errors.name?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Slug</FormLabel>
          <FormControl isInvalid={!!errors.slug?.length}>
            <Input
              value={form.slug}
              onChange={(e) => {
                setSlugTouched(true)
                updateField('slug', slugify(e.target.value))
              }}
              placeholder="auto-generated from name"
            />
            <FormHelperText>URL-friendly identifier. Auto-filled from Name, but editable.</FormHelperText>
            <FormErrorMessage>{errors.slug?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="center">
          <FormLabel pt={2} fontWeight="medium" mb={0}>Active</FormLabel>
          <FormControl>
            <Checkbox isChecked={form.active} onChange={(e) => updateField('active', e.target.checked)} colorScheme="blue">
              <Box as="span">Available for selection.</Box>
            </Checkbox>
          </FormControl>
        </Grid>

        <HStack spacing={4} justify="flex-end" pt={4}>
          {onCancel && (
            <Button variant="outline" onClick={onCancel} isDisabled={saving}>
              Cancel
            </Button>
          )}
          <Button type="submit" colorScheme="blue" isLoading={saving} loadingText={tagId ? 'Saving...' : 'Creating...'}>
            {tagId ? 'Save' : 'Create Tag'}
          </Button>
        </HStack>
      </VStack>
    </Box>
  )
}
